/**
 * Add Tool Result - SDK-compatible shim
 *
 * Mimics the AI SDK's addToolResult() API while using our manual
 * fetch implementation. This allows for future migration to useChat
 * with minimal code changes.
 */

import type { UiMessage, TextPart, ToolCallPart } from './types';

/**
 * Helper to extract text content from parts
 */
function getTextContent(parts: Array<TextPart | ToolCallPart>): string {
  return parts
    .filter((p): p is TextPart => p.type === 'text')
    .map((p) => p.text)
    .join('');
}

export interface AddToolResultOptions {
  toolCallId: string;
  result: unknown;
  conversation: UiMessage[];
  context?: {
    businessId?: string;
    teamId?: string;
    userId?: string;
    [key: string]: unknown;
  };
  signal?: AbortSignal;
}

/**
 * Sends a tool result back to the AI and continues the conversation
 *
 * This function:
 * 1. Sends the tool result to the API
 * 2. Returns the response stream
 * 3. Caller should parse the stream using the same SSE parser
 *
 * @example
 * ```tsx
 * const stream = await addToolResult({
 *   toolCallId: part.toolCallId,
 *   result: { confirmed: true, data: proposal },
 *   conversation: messages,
 *   context: { businessId, teamId }
 * });
 *
 * // Parse stream with existing SSE parser
 * parseStreamIntoMessages(stream);
 * ```
 */
export async function addToolResult({
  toolCallId,
  result,
  conversation,
  context = {},
  signal,
}: AddToolResultOptions): Promise<ReadableStream<Uint8Array> | null> {
  // Get auth token
  const { supabase } = await import('@/integrations/supabase/client');
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  // Send tool result to API
  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      // Tool result payload
      toolResult: {
        toolCallId,
        result,
      },
      // Conversation history - convert parts to content string for AI SDK
      messages: conversation.map((msg) => ({
        role: msg.role,
        content: getTextContent(msg.parts),
      })),
      // Context
      business_id: context.businessId,
      team_id: context.teamId,
      user_id: context.userId,
      ...context,
    }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to add tool result: ${response.status} - ${errorText}`
    );
  }

  return response.body;
}
